import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Eye, Code, AlertCircle, Send } from 'lucide-react';

import { docsService } from '../../services/docs/docsService';
import type {
  DocsProcedure,
  ProcedureRevision,
  ProcedureType,
} from '../../services/docs/types';

const PROCEDURE_TYPE_OPTIONS: { value: ProcedureType; label: string }[] = [
  { value: 'procedure', label: 'Procedure' },
  { value: 'engineering_document', label: 'Engineering Document' },
  { value: 'form', label: 'Form' },
  { value: 'policy', label: 'Policy' },
];

const DocsProcedureEditPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const [procedure, setProcedure] = useState<DocsProcedure | null>(null);
  const [revision, setRevision] = useState<ProcedureRevision | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  // Editable fields
  const [title, setTitle] = useState('');
  const [summary, setSummary] = useState('');
  const [type, setType] = useState<ProcedureType>('procedure');
  const [contentPlainText, setContentPlainText] = useState('');
  const [changeSummary, setChangeSummary] = useState('');
  const [activeTab, setActiveTab] = useState<'editor' | 'preview'>('editor');
  const [metaDirty, setMetaDirty] = useState(false);
  const [contentDirty, setContentDirty] = useState(false);

  useEffect(() => {
    if (!slug) return;

    const fetchProcedure = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await docsService.getProcedure(slug);
        setProcedure(data.procedure);

        setTitle(data.procedure.title);
        setSummary(data.procedure.summary ?? '');
        setType(data.procedure.type);

        if (data.revision && data.revision.approvalStatus === 'draft') {
          setRevision(data.revision);
          setContentPlainText(data.revision.contentPlainText ?? '');
          setChangeSummary(data.revision.changeSummary ?? '');
        } else {
          const draftRes = await docsService.createProcedureDraft(
            data.procedure.id
          );
          setRevision(draftRes.revision);
          setContentPlainText(draftRes.revision.contentPlainText ?? '');
        }
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to load procedure'
        );
      } finally {
        setLoading(false);
      }
    };

    fetchProcedure();
  }, [slug]);

  const handleSave = useCallback(async () => {
    if (!procedure) return;
    try {
      setSaving(true);
      setError(null);
      setSaveMessage(null);

      if (metaDirty) {
        await docsService.updateProcedure(procedure.id, {
          title: title.trim(),
          summary: summary.trim() || null,
          type,
        });
        setMetaDirty(false);
      }

      if (contentDirty && revision) {
        const res = await docsService.updateProcedureDraft(procedure.id, {
          contentPlainText,
          changeSummary: changeSummary.trim() || null,
        });
        setRevision(res.revision);
        setContentDirty(false);
      }

      setSaveMessage('Saved successfully');
      setTimeout(() => setSaveMessage(null), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  }, [
    procedure,
    revision,
    title,
    summary,
    type,
    contentPlainText,
    changeSummary,
    metaDirty,
    contentDirty,
  ]);

  const isDirty = metaDirty || contentDirty;

  const handleSubmit = useCallback(async () => {
    if (!procedure) return;
    try {
      if (metaDirty || contentDirty) await handleSave();
      await docsService.submitProcedureRevision(procedure.id);
      navigate(`/docs/procedures/${slug}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit');
    }
  }, [procedure, metaDirty, contentDirty, handleSave, navigate, slug]);

  if (loading) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-1/3" />
          <div className="h-64 bg-gray-200 rounded" />
        </div>
      </div>
    );
  }

  if (error && !procedure) {
    return (
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <p className="text-red-700">{error}</p>
          <button
            onClick={() => navigate(-1)}
            className="mt-4 text-blue-600 hover:text-blue-800 text-sm"
          >
            Go back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => navigate(`/docs/procedures/${slug}`)}
          className="flex items-center text-sm text-gray-500 hover:text-gray-700"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Back to procedure
        </button>
        <div className="flex items-center gap-2">
          {saveMessage && (
            <span className="text-sm text-green-600">{saveMessage}</span>
          )}
          {error && (
            <span className="flex items-center text-sm text-red-600">
              <AlertCircle className="w-4 h-4 mr-1" />
              {error}
            </span>
          )}
          <button
            onClick={handleSave}
            disabled={saving || !isDirty}
            className={`flex items-center px-4 py-2 text-sm rounded-lg ${
              isDirty
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
          >
            <Save className="w-4 h-4 mr-1.5" />
            {saving ? 'Saving...' : 'Save Draft'}
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="flex items-center px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            <Send className="w-4 h-4 mr-1.5" />
            Submit for Review
          </button>
        </div>
      </div>

      {/* Title, summary, type */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Title
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value);
                  setMetaDirty(true);
                }}
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type
              </label>
              <select
                value={type}
                onChange={(e) => {
                  setType(e.target.value as ProcedureType);
                  setMetaDirty(true);
                }}
                className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {PROCEDURE_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Summary
            </label>
            <input
              type="text"
              value={summary}
              onChange={(e) => {
                setSummary(e.target.value);
                setMetaDirty(true);
              }}
              placeholder="Brief description"
              className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Content editor with tabs */}
      <div className="bg-white rounded-lg border border-gray-200 mb-6">
        <div className="flex border-b border-gray-200">
          <button
            onClick={() => setActiveTab('editor')}
            className={`flex items-center px-4 py-3 text-sm font-medium border-b-2 ${
              activeTab === 'editor'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Code className="w-4 h-4 mr-1.5" />
            Content
          </button>
          <button
            onClick={() => setActiveTab('preview')}
            className={`flex items-center px-4 py-3 text-sm font-medium border-b-2 ${
              activeTab === 'preview'
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            <Eye className="w-4 h-4 mr-1.5" />
            Preview
          </button>
        </div>

        <div className="p-4">
          {activeTab === 'editor' ? (
            <textarea
              value={contentPlainText}
              onChange={(e) => {
                setContentPlainText(e.target.value);
                setContentDirty(true);
              }}
              placeholder="Write procedure content here..."
              className="w-full h-96 font-mono text-sm p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-y"
              spellCheck
            />
          ) : (
            <div className="min-h-[24rem] prose prose-sm max-w-none p-3">
              {contentPlainText.trim() ? (
                <div className="whitespace-pre-wrap">{contentPlainText}</div>
              ) : (
                <div className="flex items-center justify-center h-96 text-gray-400 text-sm">
                  No content to preview.
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Change summary */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Change Summary
        </label>
        <input
          type="text"
          value={changeSummary}
          onChange={(e) => {
            setChangeSummary(e.target.value);
            setContentDirty(true);
          }}
          placeholder="Describe what changed in this revision..."
          className="block w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        />
      </div>
    </div>
  );
};

export default DocsProcedureEditPage;
